import React, { useState } from 'react';
import { Mail, ShieldCheck, Scale, Award, FileText, Send } from 'lucide-react';

export type Product = {
  id: number;
  name: string;
  grade: string;
  packagingOptions: string[];
  description: string;
  image: string;
  applications: string[];
};

const commercialProducts: Product[] = [
  {
    id: 1,
    name: 'Fresh White Button Mushrooms',
    grade: 'Premium Commercial / Grade A',
    packagingOptions: ['200g Retail Punnets', 'Bulk Crates (Crushed-Ice Logistical Layering)'],
    description: 'Cultivated under strict climate-controlled environments with high-density physical sorting. Perfect for institutional distributors, hotels, cold-chain retail groups, and commercial kitchens requiring pristine white caps and minimal moisture variations.',
    image: '/images/Buttonm.webp',
    applications: ['HoReCa Sector Procurement', 'Fresh Retail Supply Chains', 'Food Processing Units']
  },
  {
    id: 2,
    name: 'Gourmet Dried Oyster Mushrooms',
    grade: 'Export Grade / Dehydrated Whole & Slices',
    packagingOptions: ['500g Moisture-Sealed Pouches', '10kg Bulk Commercial Boxes'],
    description: 'Dehydrated via precise temperature profiling to lock in maximum umami content, color preservation, and a prolonged shelf-life. Fully optimized for global supply lines, blending hubs, and bulk value-added food manufacturers.',
    image: '/images/dried.webp',
    applications: ['Global Dehydrated Exports', 'Instant Soup & Culinary Mixes', 'Nutraceutical Formulations']
  },
  {
    id: 3,
    name: 'Premium Dried Mushroom Powder',
    grade: 'Fine Culinary & Nutraceutical Mesh',
    packagingOptions: ['Bulk Food-Grade Sealed Drums', 'Custom Private Label Packaging'],
    description: '100% pure dehydrated mushroom fruiting bodies milled into a highly soluble, fine-mesh powder. Zero additives, fillers, or synthetic carriers. Highly sought after by seasoning creators, functional food brands, and supplement producers.',
    image: '/images/Powder.webp',
    applications: ['Umami Seasoning Blends', 'Protein Powders & Health Supplements', 'Commercial Commercial Baking Packets']
  }
];

const B2BStorefront = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const targetEmail = "wardhamushroom@gmail.com";

  // Generates professional pre-filled e-mail logistics queries
  const triggerEmailInquiry = (productName: string, context: string) => {
    const subject = encodeURIComponent(`B2B Supply Inquiry: ${productName} - Wardha Mushroom`);
    const body = encodeURIComponent(
      `Dear Wardha Mushroom Team,\n\nI am reaching out regarding your bulk offerings for ${productName}.\n\n` +
      `We are interested in discussing commercial terms, technical specification sheets, and supply availability for our requirements.\n\n` +
      `Please find our brief operational context below:\n` +
      `- Estimated Volume Needed: \n` +
      `- Delivery Location / Port: \n` +
      `- Anticipated Delivery Schedule: \n\n` +
      `Looking forward to your professional quotation and technical data sheet.\n\nBest regards,\n[Your Name/Enterprise]`
    );
    window.location.href = `mailto:${targetEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="bg-neutral-950 text-neutral-100 min-h-screen font-sans antialiased selection:bg-emerald-500 selection:text-black">
      
      {/* Decorative Technical Top Grid Accents */}
      <div className="absolute top-0 inset-x-0 h-96 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.08),transparent_50%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        
        {/* Header Block */}
        <header className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase bg-emerald-950/60 px-4 py-1.5 rounded-full border border-emerald-800/40">
            Institutional Supply & Trade Division
          </span>
          <h1 className="text-4xl md:text-6xl font-bold font-serif tracking-tight mt-4 text-white">
            Our Commercial Range
          </h1>
          <p className="text-neutral-400 text-sm md:text-base mt-4 leading-relaxed">
            We supply high-capacity volumes of premium mushroom varieties and derivatives to food processors, pharmaceutical innovators, and wholesale supply chain networks globally.
          </p>
          <div className="w-16 h-1 bg-emerald-500 mx-auto mt-6 rounded" />
        </header>

        {/* Industrial Advantages Row */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 text-xs md:text-sm text-neutral-400">
          <div className="flex items-start gap-3 p-5 rounded-xl bg-neutral-900/40 border border-neutral-900">
            <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p><strong className="text-neutral-200 block mb-0.5">Rigorous Compliance</strong> Phytosanitary verification protocols matching industrial processing parameters.</p>
          </div>
          <div className="flex items-start gap-3 p-5 rounded-xl bg-neutral-900/40 border border-neutral-900">
            <Scale className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p><strong className="text-neutral-200 block mb-0.5">Scalable Multi-Ton Logistics</strong> Flexible contracting options with prioritized volume allocation schedules.</p>
          </div>
          <div className="flex items-start gap-3 p-5 rounded-xl bg-neutral-900/40 border border-neutral-900">
            <Award className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <p><strong className="text-neutral-200 block mb-0.5">Pure Extraction Guarantee</strong> Zero artificial binding fillers or additives in processed lines.</p>
          </div>
        </section>

        {/* Product Catalog Cards Layout */}
        <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {commercialProducts.map((product) => (
            <article 
              key={product.id}
              className="bg-neutral-900/30 border border-neutral-900 rounded-3xl overflow-hidden hover:border-neutral-800 transition-all duration-300 flex flex-col justify-between backdrop-blur-sm group"
            >
              <div>
                <div className="relative h-64 overflow-hidden bg-neutral-950">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-500" 
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 text-[10px] font-mono font-bold tracking-wider text-emerald-400 bg-neutral-950/90 border border-neutral-800 px-2.5 py-1 rounded-md">
                    {product.grade}
                  </span>
                </div>

                <div className="p-6">
                  <h2 className="text-2xl font-bold text-white tracking-wide mb-3">{product.name}</h2>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-6 text-justify">{product.description}</p>
                  
                  {/* Scope of Application Badges */}
                  <div className="mb-6">
                    <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-wider block mb-2">Key Applications</span>
                    <div className="flex flex-wrap gap-1.5">
                      {product.applications.map((app, i) => (
                        <span key={i} className="text-xs bg-neutral-900 text-neutral-300 px-2.5 py-1 rounded-md border border-neutral-800/60">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="p-6 pt-0 grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedProduct(product)}
                  className="flex items-center justify-center gap-2 text-xs font-mono text-neutral-300 bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 py-3 px-4 rounded-xl transition-all"
                >
                  <FileText className="w-4 h-4 text-neutral-400" />
                  Specs Details
                </button>
                <button
                  onClick={() => triggerEmailInquiry(product.name, 'Direct Commercial Inquiry')}
                  className="flex items-center justify-center gap-2 text-xs font-mono font-bold text-neutral-950 bg-emerald-400 hover:bg-emerald-300 py-3 px-4 rounded-xl transition-all shadow-lg shadow-emerald-500/10"
                >
                  <Mail className="w-4 h-4" />
                  Contact Deal
                </button>
              </div>
            </article>
          ))}
        </main>

        {/* Global Commercial Inquiry Bar */}
        <section className="mt-16 p-8 rounded-3xl bg-gradient-to-r from-neutral-900/60 to-neutral-950 border border-neutral-800/80 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-lg md:text-xl font-bold text-white">Custom Packing or Logistics Specifications?</h3>
            <p className="text-neutral-400 text-xs md:text-sm mt-1">Connect with our institutional accounts manager to secure consistent container space or continuous contract cycles.</p>
          </div>
          <button
            onClick={() => triggerEmailInquiry('General Commercial Catalog Portfolio', 'Universal System-wide Contract Query')}
            className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider bg-white text-neutral-950 hover:bg-neutral-200 px-6 py-3.5 rounded-xl transition-all whitespace-nowrap"
          >
            <Send className="w-4 h-4" />
            Inquire Bulk Order
          </button>
        </section>

      </div>

      {/* Corporate Specification Lightbox Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-neutral-950/80 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-fadeIn">
          <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 rounded-3xl max-w-xl w-full shadow-2xl relative">
            
            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest block mb-1">
              {selectedProduct.grade}
            </span>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4">{selectedProduct.name}</h3>
            
            <div className="h-44 rounded-xl overflow-hidden mb-4 bg-neutral-950">
              <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover opacity-90" />
            </div>

            <div className="mb-6">
              <span className="text-xs font-mono text-neutral-400 uppercase tracking-wider block mb-2">Available Supply Packaging Options</span>
              <ul className="space-y-1.5 text-sm text-neutral-300">
                {selectedProduct.packagingOptions.map((opt, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    {opt}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-neutral-400 leading-relaxed text-justify mb-6 border-t border-neutral-800/80 pt-4">
              Our continuous bulk infrastructure pipeline satisfies processing integration requirements, and commercial compliance standards. Contact our procurement desk below for complete pricing structures and contract availability.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { triggerEmailInquiry(selectedProduct.name, 'In-Depth Technical Sheet Request'); }}
                className="flex items-center justify-center gap-2 text-xs font-mono font-bold text-neutral-950 bg-emerald-400 hover:bg-emerald-300 py-3 rounded-xl transition-all"
              >
                <Mail className="w-4 h-4" />
                Contact for Info
              </button>
              <button
                onClick={() => setSelectedProduct(null)}
                className="text-xs font-mono text-neutral-400 bg-neutral-950 hover:bg-neutral-950/50 border border-neutral-800/60 py-3 rounded-xl transition-all"
              >
                Return to Directory
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BStorefront;